import type { TodayCalendarItem, TodayPlanItem } from '@fos/shared/contracts/today';
import { CalendarCheck } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { ListRow } from '../../../ui/ListRow';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ListSkeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { ContextLabel } from '../../clients/components/ClientLabel';
import { shortTime } from '../calendar';

/**
 * „Plan na dziś” (widok Dzień, DECISIONS 2026-10-09): jedna lista wg godzin — spotkania dnia (kalendarz iCal i moduł
 * Spotkania; godzina + tytuł, klik = karta spotkania), potem zadania po terminie i na dziś (bez godziny). Kolumna godziny
 * tylko, gdy są spotkania. Pusty stan tylko wtedy, gdy nie ma ani spotkań, ani zadań. Spotkanie, które już się skończyło, jest wyszarzone.
 * Lifting 17.10.2026: klient (kontekst) jako chip pod tytułem, stan zadania jako pigułka.
 */
export function PlanToday({
  tasks,
  meetings,
  pending,
  nowTime,
}: {
  tasks: TodayPlanItem[];
  meetings: TodayCalendarItem[];
  pending: boolean;
  /** „HH:MM” teraz (Europe/Warsaw) — spotkania zakończone przed tą godziną są wyszarzone */
  nowTime: string;
}) {
  return (
    <Section aria-labelledby="plan-today">
      <SectionHeader title={<span id="plan-today">Plan na dziś</span>} />
      {pending ? (
        <ListSkeleton rows={3} />
      ) : meetings.length === 0 && tasks.length === 0 ? (
        <EmptyState compact icon={CalendarCheck} title="Nic zaplanowanego na dziś" />
      ) : (
        <>
          {meetings.map((m) => {
            const past = (m.endTime ?? m.time ?? '') !== '' && (m.endTime ?? m.time ?? '') <= nowTime;
            return (
              <ListRow
                key={m.id}
                to={m.to}
                leading={
                  <span
                    className={cn('w-11 text-body-s font-medium tabular-nums', past ? 'text-fg-muted' : 'text-accent')}
                  >
                    {m.time ? shortTime(m.time) : ''}
                  </span>
                }
                title={<span className={cn('font-normal', past && 'text-fg-muted')}>{m.title}</span>}
                description={m.context ? <ContextLabel name={m.context} className="mt-0.5" /> : undefined}
              />
            );
          })}
          {tasks.map((task) => (
            <ListRow
              key={task.id}
              to={task.to}
              leading={
                <span className={cn('flex items-center', meetings.length > 0 && 'w-11')}>
                  <span aria-hidden className="size-[18px] rounded-full border-[1.5px] border-line-strong" />
                </span>
              }
              title={<span className="font-normal">{task.title}</span>}
              description={task.context ? <ContextLabel name={task.context} className="mt-0.5" /> : undefined}
              trailing={
                task.status ? (
                  <StatusPill tone={task.status.tone} className="shrink-0">
                    {task.status.label}
                  </StatusPill>
                ) : undefined
              }
            />
          ))}
        </>
      )}
    </Section>
  );
}
